import { useDeferredValue, useMemo } from "react";
import { PageContainer } from "components/core/basic/PageContainer";
import { type SearchQueryPerformanceV4Row } from "./api/v4SearchQueryPerformanceReportByQuery";
import { useQuery } from "@tanstack/react-query";
import { createColumnBuilder } from "components/table/columnBuilder";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../styled-system/css";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import moment from "moment";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { idsToVisibilityState } from "components/table/utils";
import { buildMetric } from "views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import { useGetV4SearchQueryPerformanceTotals } from "./api/v4SearchQueryPerformanceReportTotals";
import SearchQueryPerformanceDashboardTable from "./SearchQueryPerformanceDashboardTable";
import { Flex } from "../../../styled-system/jsx";
import { useHistory } from "react-router-dom";
import { Button } from "components/core/basic/Button";
import { useDates } from "dates/DatesContext";
const columnHelper = createColumnBuilder<SearchQueryPerformanceV4Row>();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SearchQueryPerformanceProductSection = ({ asin }: { asin: string }) => {
  const history = useHistory();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { start, end } = useDates();

  const standardMetricIds = useStandardEntityMetrics("searchQuery");

  const columns = useMemo(
    () => [
      columnHelper.accessor("search_query", {
        id: "search_query",
        header: `Search Query`,
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: ({ getValue }) => {
          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell value={getValue()} columnWidth={300} />
            </div>
          );
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "search_query",
    "search_query_score",
    "search_query_volume",
    "impressions_total",
    "impressions_brand_or_asin",
    "impressions_brand_or_asin_percent",
    "clicks_total",
    "cart_adds_brand_or_asin",
    "cart_adds_same_day_shipping_speed",
    "cart_adds_1d_shipping_speed",
    "cart_adds_2d_shipping_speed",
    "clicks_brand_or_asin",
    "clicks_same_day_shipping_speed",
    "clicks_1d_shipping_speed",
    "clicks_2d_shipping_speed",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["search_query"],
    },
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchQueryPerformanceProductSection`,
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);

  const filters = useMemo(() => {
    // const asinFilter = [
    //   {
    //     id: "asin",
    //     value: { op: "anyof", value: [asin] },
    //   },
    // ];

    return [
      ...state.columnFilters,
      // ...asinFilter
    ];
  }, [
    state.columnFilters,
    // asin
  ]);

  const totalOptionsGetter = useGetV4SearchQueryPerformanceTotals();
  const searchQueryPerformanceTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total:
        searchQueryPerformanceTotalsReportQuery.data?.data ? searchQueryPerformanceTotalsReportQuery.data.data : {},
    };
    return standardMetricIds.map((metricId) =>
      buildMetric({ marketPlace: marketPlace }, data, metricId, COMPARE_METRIC_LOOKUP[metricId]),
    );
  }, [standardMetricIds, marketPlace, searchQueryPerformanceTotalsReportQuery.data?.data]);

  return (
    <PageContainer>
      <MetricsCard
        id={"searchQueryPerformanceReport"}
        title={"Performance Overview"}
        data={{ date: [], total: searchQueryPerformanceTotalsReportQuery.data?.data }}
        loading={
          searchQueryPerformanceTotalsReportQuery.isFetching &&
          !searchQueryPerformanceTotalsReportQuery.isPlaceholderData
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          searchQueryPerformanceTotalsReportQuery.isFetching &&
          !searchQueryPerformanceTotalsReportQuery.isPlaceholderData
        }
        hideChart={true}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["search_query_score", "search_query_volume"]}
        group={"day"}
        showAnnotations={true}
        byline={
          <>
            {moment(start).format("MMMM Do YYYY")}
            {" - "}
            {moment(end).format("MMMM Do YYYY")}
          </>
        }
        context={"searchQuery"}
        inlineStatus={
          <Flex className={css({ gap: "small", alignItems: "center", paddingX: "large", whiteSpace: "nowrap" })}>
            <Button
              variant={"link"}
              className={css({ paddingStart: "large", fontSize: "xl" })}
              onPress={() => history.push("/user/searchqueryperformance")}
            >
              View Uploaded Reports
            </Button>
          </Flex>
        }
      >
        <SearchQueryPerformanceDashboardTable
          state={state}
          initialState={initialState}
          setters={setters}
          // selectedAsins={[asin]}
          selectedAsins={[]}
          renderAsinsFilter={false}
          renderExpander={false}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

export default SearchQueryPerformanceProductSection;
